/**
 * The top of the project's Risks part (Work → Risks): the workbench Watchlist's own pieces on the
 * console's data, in the order its RisksView has them — the picture of how an entry moves
 * (WatchlistMap.tsx), the register's numbers (watchlistModel `kpisFor`: what is above appetite and
 * which reviews are overdue), then the heat map (RiskMatrix.tsx) beside the thresholds and the
 * appetite at each level. Pressing a cell, a band or a step narrows the list below to those entries.
 *
 * The entries are `GET /projects/v1/:id/risks`, the template and appetite
 * `GET /projects/v1/:id/risks/settings` (the built-in ISO 31000 5×5 until a project saves its own).
 */
import type { RiskEntry, RiskKind, RiskSettings } from '@aioe/shared';
import { templateFor } from '@aioe/shared/risks/standard';
import { LayoutCells, ListUl } from '@gravity-ui/icons';
import { Segment } from '@heroui-pro/react';
import { useMemo, useState } from 'react';

import { ArtStat, statNumber } from '../ui/Art.tsx';
import { AppetitePanel, type MatrixView, RiskMatrix, ThresholdsPanel } from './RiskMatrix.tsx';
import { rowFromEntry } from './risksModel.ts';
import { atStage } from './riskWorkflowModel.ts';
import { kpisFor, uidsInBand } from './watchlistModel.ts';
import { type StageFilter, WatchlistMap } from './WatchlistMap.tsx';
// After RiskMatrix.tsx's risks.css, so its narrow-window rules win.
import './riskOverview.css';

/** What the list below is narrowed to: a cell's or a band's entries, or the entries at a step. */
export interface RiskFocus {
  label: string;
  /** The entries' uids (a cell or a band of the matrix). */
  uids?: readonly string[];
  /** The entries at a step of their lane ("Show me" on the picture). */
  stage?: { kind: RiskKind; stage: StageFilter['stage'] };
  /** The cell pressed, to show it as on. */
  cell?: { likelihood: number; impact: number };
}

/** Whether an entry is among those the focus names. */
export function inFocus(e: RiskEntry, f: RiskFocus | undefined): boolean {
  if (!f) return true;
  if (f.uids) return f.uids.includes(e.uid);
  if (f.stage) return atStage(e, f.stage.kind, f.stage.stage);
  return true;
}

type Mode = 'heatmap' | 'list';
const MODE_KEY = 'aioe.risks.mode';

function readMode(): Mode {
  try {
    return localStorage.getItem(MODE_KEY) === 'list' ? 'list' : 'heatmap';
  } catch {
    return 'heatmap';
  }
}

export function RiskOverview({ entries, settings, now, tone, focus, onFocus }: { entries: readonly RiskEntry[]; settings: RiskSettings | undefined; now: number; tone: 'blue'; focus?: RiskFocus; onFocus: (f: RiskFocus | undefined) => void }) {
  const [mode, setModeState] = useState<Mode>(readMode);
  const setMode = (next: Mode) => {
    setModeState(next);
    onFocus(undefined);
    try {
      localStorage.setItem(MODE_KEY, next);
    } catch {
      /* a per-viewer convenience only */
    }
  };
  const [matrix, setMatrix] = useState<MatrixView>({ which: 'residual', dimension: 'overall' });
  const template = templateFor(settings, '');
  const rows = useMemo(() => entries.map((e) => rowFromEntry(e, { rootId: '' }, entries)), [entries]);
  const risks = useMemo(() => entries.filter((e) => e.kind === 'risk'), [entries]);
  const heatmap = mode === 'heatmap' && risks.length > 0;
  // The register's numbers for risks on the heat map (one per band), for everything on the list.
  const kpis = useMemo(() => kpisFor(heatmap ? 'risk' : 'all', rows, template, settings, now, matrix.which), [heatmap, rows, template, settings, now, matrix.which]);
  const all = useMemo(() => kpisFor('all', rows, template, settings, now, matrix.which), [rows, template, settings, now, matrix.which]);
  // Above appetite and reviews overdue are what wait on someone: shown with the bands too.
  const shown = heatmap ? [...kpis.filter((k) => k.id !== 'total' && k.id !== 'closed'), ...all.filter((k) => k.id === 'appetite' || k.id === 'reviews')] : kpis;

  return (
    <>
      <WatchlistMap rows={rows} tone={tone} onShowMe={(s) => onFocus({ label: s.label, stage: { kind: s.kind, stage: s.stage } })} />
      {risks.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2" data-risk-toolbar>
          <Segment aria-label="Heat map or list" size="sm" selectedKey={mode} onSelectionChange={(key) => setMode(String(key) === 'list' ? 'list' : 'heatmap')} data-risk-mode>
            <Segment.Item id="heatmap">
              <LayoutCells className="size-3.5" /> Heat map
            </Segment.Item>
            <Segment.Item id="list">
              <ListUl className="size-3.5" /> List
            </Segment.Item>
          </Segment>
          <span className="ml-auto text-xs text-muted" data-risk-template>
            Scale: {template.name}
          </span>
        </div>
      ) : null}
      <div className="@container">
        <div className="risk-kpis" data-risk-stats>
          {shown.map((k) => (
            <ArtStat
              key={k.id}
              tone={k.tone}
              label={k.label}
              value={statNumber(k.value)}
              detail={k.detail}
              needsYou={k.needsYou && k.value > 0}
              {...(k.band && heatmap
                ? {
                    selected: focus?.label === k.label,
                    onPress: () => onFocus(focus?.label === k.label ? undefined : { label: k.label, uids: uidsInBand(rows, template, k.band!, matrix.which) }),
                  }
                : {})}
            />
          ))}
        </div>
      </div>
      {heatmap ? (
        <div className="@container">
          <div className="grid gap-3 @4xl:grid-cols-[minmax(0,1fr)_17rem]">
            <RiskMatrix
              entries={risks}
              template={template}
              settings={settings}
              view={matrix}
              onView={(next) => {
                setMatrix(next);
                onFocus(undefined);
              }}
              {...(focus?.cell ? { selected: focus.cell } : {})}
              onCell={(cell, label) => onFocus(cell ? { label: label ?? `${cell.likelihood} × ${cell.impact}`, uids: cell.uids, cell: { likelihood: cell.likelihood, impact: cell.impact } } : undefined)}
              tone={tone}
              icon={<LayoutCells />}
            />
            <div className="flex flex-col gap-3">
              <ThresholdsPanel template={template} />
              <AppetitePanel template={template} settings={settings} rootId="" />
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
